import { PublicIdSchemas } from '@idc-forms/id'
import { z } from 'zod'
import { requiredString } from '../primitives/string'
import { InstantSchema } from '../primitives/time'
import { V2CursorPaginationSchema } from './http'

/**
 * Contrato de la administración v2 de sociedades. Cuarto catálogo del backoffice
 * en v2 y el segundo con una relación en el listado: una sociedad (entidad legal
 * que posee proyectos y firma por ellos) pertenece a una empresa, así que su
 * empresa madre viaja como referencia `cmp_` en vez del ObjectId legacy —el mismo
 * molde de `$lookup` que estrenaron las agencias.
 *
 * `name` es único GLOBAL (a diferencia del `code` de las agencias, que es único
 * por empresa).
 *
 * Las credenciales SAP (`dbName`/`dbUser`/`dbPassword`) son WRITE-ONLY: el API las
 * guarda encriptadas (AES-256-GCM) y NUNCA las devuelve —ni en claro ni
 * enmascaradas—. El listado y el detalle sólo exponen `sapConfigured`, un booleano
 * derivado que le dice a la UI si la sociedad tiene sus credenciales cargadas sin
 * filtrar su longitud ni su contenido.
 */

/**
 * Referencia a la empresa madre, wire-safe: el `_id` no cruza el adapter, así que
 * se publica su `cmp_`. Nullable por robustez del listado —igual que la autoría—:
 * una fila con la empresa sin `publicId` presenta `null` en vez de tumbar la
 * página entera; `company` es required en el dominio, así que en la práctica
 * siempre se resuelve.
 */
export const V2AdminSocietyCompanySchema = z
  .object({
    id: PublicIdSchemas.company,
    name: z.string()
  })
  .nullable()

/** Autoría wire-safe: `usr_`, nunca el ObjectId. Nullable como en el resto de v2. */
export const V2AdminSocietyAuthorSchema = z
  .object({
    id: PublicIdSchemas.user,
    fullname: z.string()
  })
  .nullable()

/** Fila del listado y del detalle. Las credenciales SAP nunca aparecen acá. */
export const V2AdminSocietySchema = z.object({
  id: PublicIdSchemas.society,
  name: z.string().min(1),
  company: V2AdminSocietyCompanySchema,
  active: z.boolean(),
  /**
   * Derivado y de sólo lectura: `true` si la sociedad tiene sus tres credenciales
   * SAP cargadas. La UI lo usa para mostrar «configurado» sin recibir el secreto.
   */
  sapConfigured: z.boolean(),
  createdBy: V2AdminSocietyAuthorSchema,
  updatedBy: V2AdminSocietyAuthorSchema,
  createdAt: InstantSchema,
  updatedAt: InstantSchema
})

/** Página cursor-paginada del listado (ADR 0019). */
export const V2AdminSocietiesPageSchema = z.object({
  data: z.array(V2AdminSocietySchema),
  pagination: V2CursorPaginationSchema
})

/**
 * Total y faceta `active` con los mismos filtros allowlist del listado. La
 * faceta ignora el filtro de su propia dimensión; `total` respeta la consulta
 * completa. La empresa no es una faceta: se filtra por nombre (texto), no es una
 * dimensión de baja cardinalidad con valor de conteo.
 */
export const V2AdminSocietiesSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  active: z.object({
    true: z.number().int().nonnegative(),
    false: z.number().int().nonnegative()
  })
})

/**
 * Entrada mínima del selector de sociedades. `company` (`cmp_`) permite a los
 * formularios que cascadean por empresa filtrar el select.
 */
export const V2AdminSocietyLookupItemSchema = z.object({
  id: PublicIdSchemas.society,
  name: z.string().min(1),
  company: PublicIdSchemas.company
})

export const V2AdminSocietyLookupSchema = z.object({
  data: z.array(V2AdminSocietyLookupItemSchema)
})

/**
 * Alta de sociedad. La empresa madre entra por `cmp_` (no por ObjectId como el
 * wire legacy); el service la resuelve y valida que exista. `name` se recorta y
 * debe ser único (case-insensitive) globalmente. Las credenciales SAP son
 * obligatorias al crear —una sociedad sin ellas no puede conectarse a su base— y
 * write-only: viajan de entrada y nunca de vuelta.
 */
export const V2CreateAdminSocietySchema = z.object({
  name: requiredString()
    .transform((value) => value.trim())
    .pipe(requiredString()),
  company: PublicIdSchemas.company,
  dbName: requiredString(),
  dbUser: requiredString(),
  dbPassword: requiredString(),
  active: z.boolean().optional()
})

/**
 * Alta de la sociedad de DEMOSTRACIÓN (ADR 0041), sólo para quien puede ver la demo. Nace ya
 * marcada `isDemo` y SIN credenciales SAP: por eso no hay campos `db*` acá, y por eso es un
 * contrato aparte y no un flag sobre el alta normal (que las exige y no debería poder aflojarse).
 */
export const V2CreateAdminDemoSocietySchema = z.object({
  name: requiredString()
    .transform((value) => value.trim())
    .pipe(requiredString()),
  company: PublicIdSchemas.company
})

/**
 * Alta de la negociación de DEMOSTRACIÓN (ADR 0041): una venta ya CERRADA sobre un inmueble de la
 * sociedad demo, con el cliente de revisión de tiendas como titular. Sólo para visores de demo.
 */
export const V2CreateAdminDemoNegotiationSchema = z.object({
  property: PublicIdSchemas.property,
  client: PublicIdSchemas.client
})

/** `created: false` = el inmueble ya tenía una negociación y no se escribió nada (idempotente). */
export const V2AdminDemoNegotiationSchema = z.object({
  id: PublicIdSchemas.negotiation,
  code: z.number().int(),
  created: z.boolean()
})

/**
 * Estado de la cuenta de revisión de tiendas respecto de su «primer ingreso». Sólo lo responde un
 * visor de demo y sólo para ESA cuenta: para cualquier otro caso es un 404, que además es la señal
 * con la que la web decide si dibuja la tarjeta.
 */
export const V2AdminDemoFirstRunStatusSchema = z.object({
  accountId: PublicIdSchemas.clientAccount,
  email: z.string(),
  acceptances: z.number().int().nonnegative(),
  activeSessions: z.number().int().nonnegative(),
  enabledPushTokens: z.number().int().nonnegative(),
  hasCustomPreferences: z.boolean()
})

/** Lo que el reinicio hizo. Todo en cero = la cuenta ya estaba como nueva (idempotente). */
export const V2AdminDemoFirstRunResetSchema = z.object({
  acceptancesRemoved: z.number().int().nonnegative(),
  sessionsRevoked: z.number().int().nonnegative(),
  pushTokensDisabled: z.number().int().nonnegative()
})

/**
 * Credencial omitida O VACÍA = conservar la encriptada actual: `''` se normaliza a
 * `undefined` antes de validar, así el form de edición sólo re-escribe lo que el
 * usuario efectivamente vuelve a tipear.
 */
const emptyMeansKeep = z.preprocess(
  (value) => (value === '' ? undefined : value),
  requiredString().optional()
)

/** Edición parcial. Un id desconocido responde 404: nunca hace upsert. */
export const V2UpdateAdminSocietySchema = V2CreateAdminSocietySchema.partial().extend({
  dbName: emptyMeansKeep,
  dbUser: emptyMeansKeep,
  dbPassword: emptyMeansKeep
})

export type V2AdminSocietyCompany = z.infer<typeof V2AdminSocietyCompanySchema>
export type V2AdminSocietyAuthor = z.infer<typeof V2AdminSocietyAuthorSchema>
export type V2AdminSociety = z.infer<typeof V2AdminSocietySchema>
export type V2AdminSocietiesPage = z.infer<typeof V2AdminSocietiesPageSchema>
export type V2AdminSocietiesSummary = z.infer<typeof V2AdminSocietiesSummarySchema>
export type V2AdminSocietyLookupItem = z.infer<typeof V2AdminSocietyLookupItemSchema>
export type V2AdminSocietyLookup = z.infer<typeof V2AdminSocietyLookupSchema>
export type V2CreateAdminSocietyInput = z.infer<typeof V2CreateAdminSocietySchema>
export type V2CreateAdminDemoNegotiationInput = z.infer<typeof V2CreateAdminDemoNegotiationSchema>
export type V2AdminDemoNegotiation = z.infer<typeof V2AdminDemoNegotiationSchema>
export type V2AdminDemoFirstRunStatus = z.infer<typeof V2AdminDemoFirstRunStatusSchema>
export type V2AdminDemoFirstRunReset = z.infer<typeof V2AdminDemoFirstRunResetSchema>
export type V2CreateAdminDemoSocietyInput = z.infer<typeof V2CreateAdminDemoSocietySchema>
export type V2UpdateAdminSocietyInput = z.infer<typeof V2UpdateAdminSocietySchema>
